import {
  Input,
  makeStyles,
  MenuItem,
  Select,
  Tooltip,
} from '@material-ui/core';
import { styled } from '@material-ui/core/styles';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import {
  ALLOWED_OPERATIONAL_BILLING_STATUSES,
  BILLING_STATUSES,
  getBillingIssueDisplayLabel,
  getChipDisplayClass,
  getDisplayStatus,
  INACTIVE_STATUSES,
  LOAD_STATUS,
  TRACKER_VISIBLE_STATUSES,
} from 'doc-mate-store/lib/constants/load';
import { observer } from 'mobx-react-lite';
import React, { useCallback } from 'react';

import useStatusTransition from '../../../hooks/statusTransition';
import BillingIssueModal from './BillingIssueModal';
import InvalidStatusTransitionModal from './InvalidStatusTransitionModal';
import OrderCancellationConfirmation from './OrderCancellationConfirmation';
import TimestampConfirmModal from './TimestampConfirmModal';

type SelectProps = {
  isDispatcher: boolean;
  status: LOAD_STATUS;
  loadId: number;
  isBilling: boolean;
  billingIssueDescription?: string;
};

const SelectChip = styled('div')({
  display: 'inline-block',
  padding: '0px 5px',
  height: '20px',
  borderRadius: '10px',
  lineHeight: '20px',
  fontSize: '12px',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  overflow: 'hidden',

  '&.unassigned': { backgroundColor: '#F38686', color: 'white' },
  '&.pickup': { backgroundColor: '#FADFA3' },
  '&.dropoff': { backgroundColor: '#A6E7C1' },
  '&.delivered': { backgroundColor: '#CAA2EE' },
  '&.completed': { backgroundColor: '#ACD5F0' },
  '&.cancelled': { backgroundColor: '#BDBDBD' },
});

export const SelectWrapper: React.FC<SelectProps> = observer(props => {
  const classes = useStyles();
  const {
    isDispatcher,
    status,
    loadId,
    isBilling,
    billingIssueDescription,
  } = props;

  const isInvoiced = status === LOAD_STATUS.INVOICED;

  const {
    openSelect,
    setOpenSelect,
    isLoading,
    newStatus,
    openCancelModal,
    openPaperworkIssueModal,
    openInvalidModal,
    invalidMessage,
    openTimestampModal,
    isManifestWideChange,
    forwardStatusesToFill,
    reverseStatusesToClear,
    timestamps,
    setTimestamps,
    handleChange,
    executeStatusUpdate,
    handleCancel,
  } = useStatusTransition(loadId, status);

  const handleOpen = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setOpenSelect(true);
    },
    [setOpenSelect],
  );

  const iconComponent = useCallback(
    () => (
      <span onClick={handleOpen}>
        {!isDispatcher ? null : openSelect ? (
          <ArrowDropUpIcon />
        ) : (
          <ArrowDropDownIcon />
        )}
      </span>
    ),
    [handleOpen, isDispatcher, openSelect],
  );

  return (
    <>
      <Select
        className={classes.select}
        fullWidth
        input={<Input disableUnderline />}
        onChange={handleChange}
        disabled={!isDispatcher || (!isBilling && isInvoiced)}
        value={status}
        open={openSelect}
        onClose={() => setOpenSelect(false)}
        IconComponent={iconComponent}
        renderValue={selected => {
          const currentStatus = selected as LOAD_STATUS;
          let chipLabel = getDisplayStatus(currentStatus);

          if (currentStatus === LOAD_STATUS.BILLING_ISSUE) {
            const isMultipleOrOther =
              billingIssueDescription && billingIssueDescription.includes(',');

            chipLabel = isMultipleOrOther
              ? 'Billing Issues - Other'
              : getBillingIssueDisplayLabel(billingIssueDescription);
          }

          const chipContent = (
            <SelectChip className={getChipDisplayClass(currentStatus)}>
              {chipLabel}
            </SelectChip>
          );

          if (currentStatus === LOAD_STATUS.BILLING_ISSUE) {
            return (
              <Tooltip
                title={billingIssueDescription ? billingIssueDescription : ''}
                arrow
                placement="top"
              >
                {chipContent}
              </Tooltip>
            );
          }

          return chipContent;
        }}
      >
        {TRACKER_VISIBLE_STATUSES.map((stat: LOAD_STATUS) => {
          const isCurrentBilling = BILLING_STATUSES.includes(status);
          const isInactive = INACTIVE_STATUSES.includes(status);
          const isTargetBilling = BILLING_STATUSES.includes(stat);
          const isOperationalAllowed = ALLOWED_OPERATIONAL_BILLING_STATUSES.includes(
            status,
          );
          const isTargetOperationalAllowed = ALLOWED_OPERATIONAL_BILLING_STATUSES.includes(
            stat,
          );

          let isDisabled = false;

          if (stat === LOAD_STATUS.PENDING_ACCEPTANCE) {
            isDisabled = true;
          } else if (
            !isBilling &&
            (stat === LOAD_STATUS.BILLING_ISSUE ||
              stat === LOAD_STATUS.INVOICED ||
              status === LOAD_STATUS.INVOICED)
          ) {
            isDisabled = true;
          } else if (isCurrentBilling) {
            if (!isTargetBilling && !isTargetOperationalAllowed) {
              isDisabled = true;
            }
          } else if (isTargetBilling) {
            if (!isOperationalAllowed) {
              isDisabled = true;
            }
          } else if (!isInactive && stat === LOAD_STATUS.UNASSIGNED) {
            isDisabled = true;
          }

          return (
            <MenuItem disabled={isDisabled} key={stat} value={stat}>
              <SelectChip className={getChipDisplayClass(stat)}>
                {getDisplayStatus(stat)}
              </SelectChip>
            </MenuItem>
          );
        })}
      </Select>

      {!openSelect && openCancelModal && (
        <OrderCancellationConfirmation
          loading={isLoading}
          onConfirm={desc => newStatus && executeStatusUpdate(newStatus, desc)}
          onCancel={handleCancel}
        />
      )}

      {!openSelect && openPaperworkIssueModal && (
        <BillingIssueModal
          loading={isLoading}
          onConfirm={desc => newStatus && executeStatusUpdate(newStatus, desc)}
          onCancel={handleCancel}
        />
      )}

      {!openSelect && openTimestampModal && (
        <TimestampConfirmModal
          open={openTimestampModal}
          isLoading={isLoading}
          newStatus={newStatus}
          isManifestWideChange={isManifestWideChange}
          forwardStatusesToFill={forwardStatusesToFill}
          reverseStatusesToClear={reverseStatusesToClear}
          timestamps={timestamps}
          onTimestampChange={(st, val) =>
            setTimestamps(prev => ({ ...prev, [st]: val }))
          }
          onConfirm={() =>
            newStatus && executeStatusUpdate(newStatus, undefined, timestamps)
          }
          onCancel={handleCancel}
        />
      )}

      {!openSelect && openInvalidModal && (
        <InvalidStatusTransitionModal
          open={openInvalidModal}
          message={invalidMessage}
          onClose={handleCancel}
        />
      )}
    </>
  );
});

const useStyles = makeStyles(() => ({
  select: {
    '& > .MuiSelect-select.MuiSelect-select': {
      backgroundColor: 'transparent',
      paddingRight: 0,
    },
  },
}));

export default SelectWrapper;
